import { createFileRoute, useParams, useSearch } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { jwtDecode } from 'jwt-decode'

import { Separator } from '@components/ui/separator'
import { Heading } from '@components/ui/typography/heading'
import { Text } from '@components/ui/typography/text'
import { VStack, HStack, Flex, Spacer } from '@components/ui/layout/flex'

import { Landing } from '@components/Landing'
import { useBranding, useForm, useResponse } from '@utils/api'
import { ErrorLanding } from '@components/ErrorLanding'
import Loading from '@components/Loading'
import ResponseTokenRenewal from '@components/ResponseTokenRenewal'

// Lazy load SignatureWizard to defer loading heavy PDF dependencies (~3MB)
const SignatureWizard = lazy(() => import('@components/SignatureWizard'))

type FormPhase = 'landing' | 'wizard' | 'complete'

type FormSearch = {
  token?: string
}

export const Route = createFileRoute('/forms/$id')({
  component: Form,
  validateSearch: (search: Record<string, unknown>): FormSearch => {
    return {
      token: search.token as string | undefined,
    }
  },
})

function Form() {
  const { id: responseId } = useParams({ from: '/forms/$id' })
  const { token: responseToken } = useSearch({ from: '/forms/$id' })
  const { branding, isLoading: isBrandingLoading, isError: isBrandingError } = useBranding()
  const {
    response,
    isLoading: isResponseLoading,
    isError: isResponseError,
  } = useResponse({
    id: responseId,
    fetcherOptions: { headers: { Authorization: `Bearer ${responseToken}` } },
  })
  const { form, isLoading: isFormLoading } = useForm(response?.formId, {
    language: response?.languagePreference,
  })
  const { t, i18n } = useTranslation()
  const [phase, setPhase] = useState<FormPhase>('landing')

  useEffect(() => {
    if (!responseToken) return

    try {
      const decodedToken: any = jwtDecode(responseToken)
      if (decodedToken && decodedToken.language) {
        i18n.changeLanguage(decodedToken.language)
      }
    } catch (error) {
      console.error('Failed to decode token', error)
    }
  }, [responseToken, i18n])

  const calculateDuration = () => {
    const formNodes = form?.lastPublishedRevision?.formNodes
    if (!formNodes) return 1

    return formNodes.reduce((accumulator, node) => {
      switch (node.nodeSubtype) {
        case 'open_ended_long':
          return accumulator + 2
        case 'multiple_choice':
          return accumulator + 0.5
        case 'upload':
          return accumulator + 0.5
        case 'numeric':
          return accumulator + 0.5
        case 'date':
          return accumulator + 0.5
        default:
          return accumulator
      }
    }, 1)
  }

  const renderComplete = () => {
    return (
      <Flex
        direction="row"
        align="center"
        justify="center"
        className="min-h-[100svh] p-8"
      >
        <VStack
          className="bg-white rounded-[20px] shadow-md p-8"
          gap={4}
        >
          <HStack className="inline-block">
            <Heading level={3}>
              {t('form.thanks')}
            </Heading>
            <Text className="pl-9 text-gray-500 text-xl font-semibold">
              {t('form.submissionConfirmationHeading')}
            </Text>
          </HStack>
          <Separator className="my-2.5" />
          <Spacer />
          <Text>{t('form.submissionConfirmationMessage')}</Text>
        </VStack>
      </Flex>
    )
  }

  if (isResponseLoading || isBrandingLoading || isFormLoading) {
    return <Loading />
  }

  if (isBrandingError || isResponseError) {
    if (isResponseError?.status === 403) {
      return <ResponseTokenRenewal responseId={responseId} />
    }

    return (
      <ErrorLanding
        heading={t('errorPage.general.heading')}
        subHeading={t('errorPage.general.subheading')}
        description={t('errorPage.general.description')}
        footer={t('errorPage.general.footer')}
      />
    )
  }

  return (
    <div className={`animate-in fade-in duration-300 ${response && branding ? 'opacity-100' : 'opacity-0'}`}>
      <Flex
        direction="row"
        align="center"
        justify="center"
        className="min-h-[100svh] overflow-y-auto px-2 py-4 w-full"
        style={{
          backgroundColor: branding?.backgroundColor,
          backgroundImage: `radial-gradient(center center, circle cover, ${branding?.backgroundColor}, transparent 30%)`,
        }}
      >
        {phase === 'landing' && (
          <Landing
            setShowWizard={() => setPhase('wizard')}
            title={response?.title}
            description={response?.description}
            branding={branding}
            duration={calculateDuration()}
          />
        )}
        {phase === 'wizard' && (
          <Suspense fallback={<Loading />}>
            <SignatureWizard
              responseId={responseId}
              onComplete={() => setPhase('complete')}
              token={responseToken ?? ''}
            />
          </Suspense>
        )}
        {phase === 'complete' && renderComplete()}
      </Flex>
    </div>
  )
}
